// wallet-standard-discovery.ts - PROPER native-wallet enumeration for the unified
// login widget. We do NOT hand-maintain a list of wallet names (Phantom / Solflare
// / Backpack / ...); instead we read the Solana Wallet Standard registry
// (`@wallet-standard/app` getWallets(), fed by each wallet's
// `wallet-standard:register-wallet` event) and surface every wallet that advertises
// the Solana features we need. New wallets appear automatically, with their real
// name + icon, and nothing is hardcoded.
//
// Each discovered wallet is adapted to the SDK's tiny `InjectedSolanaProvider`
// shape so the existing wallet-login path (loginWithWallet -> InjectedWalletProvider,
// SIWS) and the full app signing surface work unchanged. web3.js is only imported
// lazily inside the transaction methods, so listing wallets and message-signing
// login stay off the heavy chunk.
import { getWallets } from "@wallet-standard/app";
import { SolanaSignAndSendTransaction, SolanaSignMessage, SolanaSignTransaction, } from "@solana/wallet-standard-features";
import { StandardConnect, StandardDisconnect } from "@wallet-standard/features";
import bs58 from "bs58";
import { resolveSolanaWalletStandardChain } from "../solana-rpc";
import { isSolanaMobileWallet, solanaMobileWalletLeavesPage, } from "./solana-mobile-registration";
function hasFeature(wallet, name) {
    return Object.prototype.hasOwnProperty.call(wallet.features, name);
}
function supportsSolana(wallet) {
    return wallet.chains.some((c) => c.startsWith("solana:"));
}
/** A wallet is usable for Bounded login when it can connect + sign a SIWS message
 *  on a Solana chain. Transaction features are optional and checked lazily at
 *  call time, so a message-signing-only wallet still qualifies for login. */
function isLoginCapable(wallet) {
    return (supportsSolana(wallet) &&
        hasFeature(wallet, StandardConnect) &&
        hasFeature(wallet, SolanaSignMessage));
}
/** The Wallet-Standard chain id for a configured network, or null when it is
 *  absent/unsupported. Ordering must never throw - failing closed on an
 *  unsupported network is the signing path's job. */
function solanaChainOrNull(network) {
    switch (network) {
        case "solana_mainnet": return "solana:mainnet";
        case "solana_devnet": return "solana:devnet";
        default: return null;
    }
}
function feature(wallet, name) {
    return wallet.features[name];
}
/**
 * Adapt a Wallet Standard wallet to the slim `InjectedSolanaProvider` the SDK's
 * wallet-login + signing path consumes. Connect resolves (and caches) the active
 * account; sign* delegate to the wallet's own Solana features. Nothing is
 * wallet-name specific.
 *
 * DS2-0383: signAndSendTransaction MUST execute on the network the APP configured
 * (`config.chain`), not whatever chain the wallet happens to advertise first.
 * Deriving the chain from the wallet's advertised list (with a mainnet fallback)
 * could hand a devnet/testnet transaction to the wallet labelled mainnet - or the
 * reverse. `configuredNetwork` is resolved to the exact Wallet-Standard chain id;
 * an unconfigured network fails closed at sign time rather than defaulting.
 */
export function standardWalletToInjectedProvider(wallet, configuredNetwork) {
    // The adapter holds NO account of its own: `wallet.accounts` is the Wallet
    // Standard's live authorized-account view, kept in sync by the wallet itself
    // (it emits standard:events 'change' when it moves). Reading it on every
    // access means two things at once:
    //   - an account switch inside the wallet is OBSERVED here, so
    //     InjectedWalletProvider's DS2-0369 check still fires instead of being
    //     hidden behind a copy this adapter took at connect time;
    //   - a connection survives across adapter instances, which is what the
    //     mobile wallet needs: MWA keeps its accounts for the life of its
    //     authorization, so a later signing call reuses it rather than paying
    //     another round trip out to the wallet app.
    // Prefer an account that can work on the network the app configured: a wallet
    // can hold several, and one authorized for another cluster would sign
    // something this app can never land. Fall back to any Solana account only
    // when the app named no network.
    const wanted = configuredNetwork ? solanaChainOrNull(configuredNetwork) : null;
    // Wallet Standard declares chains AND features PER ACCOUNT, so a wallet-level
    // capability is not proof that the account we picked has it. An account with
    // an explicit feature list that omits solana:signMessage cannot complete a
    // SIWS login; one that lists nothing is unconstrained by convention.
    // STRICT. `WalletAccount.features` is the feature names that account supports,
    // and a wallet that means "the defaults" says so by omitting the field - the
    // mobile adapter fills DEFAULT_FEATURES in exactly that case. So an empty list
    // means none, and reading it as "everything" picks an account that cannot sign
    // and fails at the wallet instead of here.
    const accountSupports = (account, feature) => account.features.includes(feature);
    const pickSolanaAccount = (accounts) => {
        // With a network configured, an account that does not support it is not a
        // usable account: `WalletAccount.chains` is what that account can sign for,
        // so handing it a transaction for another chain just fails at the wallet -
        // or worse, is honoured on the wrong cluster. And a Solana account that
        // cannot sign a message cannot log in at all, so prefer one that can.
        // No usable account means NOT CONNECTED: a multi-chain wallet can advertise
        // Solana at wallet level while its authorized account is another chain
        // entirely, and taking that one would expose e.g. an EVM address as the
        // Solana public key.
        const solana = accounts.filter((a) => (a.chains.some((c) => c.startsWith("solana:")) && (!wanted || a.chains.includes(wanted))));
        // No account that can sign a message is no usable account: SIWS login is the
        // first thing every wallet path does, so falling back to one that cannot
        // sign only moves the failure somewhere less legible.
        return solana.find((a) => accountSupports(a, SolanaSignMessage));
    };
    const currentAccount = () => { var _a; return (_a = pickSolanaAccount(wallet.accounts)) !== null && _a !== void 0 ? _a : null; };
    /** A `solana:signTransaction` input, carrying the app's chain when it has one. */
    const signInputFor = (account, transaction) => (wanted ? { account, transaction, chain: wanted } : { account, transaction });
    const requireAccount = () => {
        const acc = currentAccount();
        if (!acc)
            throw new Error("Wallet not connected");
        return acc;
    };
    return {
        // This wallet reaches its signer by leaving the page (an Android intent),
        // so every operation needs a fresh user gesture. Flagging it here means the
        // SDK applies the caller's confirm hook to exactly the wallets that need it
        // - callers never have to work out which wallet they ended up with.
        leavesPage: solanaMobileWalletLeavesPage(wallet.name),
        // The transaction codec is code-split (it pulls web3.js, which must stay off
        // the login path). Loading it INSIDE a sign call would put a network fetch
        // between the user's tap and the wallet invocation, which is exactly the
        // transient activation a mobile wallet's intent navigation needs. Callers
        // warm it here, before taking that tap - and ONLY for the actions that use
        // it, so a SIWS login still never touches that chunk.
        async prepare(action) {
            if (action === "signTransaction" || action === "signAndSubmitTransaction") {
                await import("./wallet-standard-tx");
            }
        },
        get isConnected() {
            return currentAccount() !== null;
        },
        get publicKey() {
            // Wallet Standard account.address is already the base58 Solana address.
            const acc = currentAccount();
            return acc ? { toString: () => acc.address } : null;
        },
        async connect(options) {
            var _a;
            const connect = feature(wallet, StandardConnect);
            if (!connect)
                throw new Error("Wallet does not support standard:connect");
            // Map the injected-provider `onlyIfTrusted` hint onto the Wallet Standard
            // `silent` connect: an already-trusted wallet (or MWA with a cached
            // authorization) reconnects without prompting; an untrusted one returns
            // no accounts and the caller falls back to an interactive connect.
            const res = await connect.connect((options === null || options === void 0 ? void 0 : options.onlyIfTrusted) ? { silent: true } : undefined);
            // Prefer what connect returned, but fall back to the live view for a
            // wallet that only publishes newly authorized accounts there.
            const acc = (_a = pickSolanaAccount(res.accounts)) !== null && _a !== void 0 ? _a : currentAccount();
            if (!acc)
                throw new Error("Wallet returned no account on connect");
            return { publicKey: { toString: () => acc.address } };
        },
        async disconnect() {
            const disconnect = feature(wallet, StandardDisconnect);
            if (disconnect)
                await disconnect.disconnect();
        },
        async signMessage(message) {
            const acc = requireAccount();
            const sign = feature(wallet, SolanaSignMessage);
            if (!sign)
                throw new Error("Wallet does not support solana:signMessage");
            const [out] = await sign.signMessage({ account: acc, message });
            return { signature: out.signature, publicKey: { toString: () => acc.address } };
        },
        // DS2-0383, on the plain-sign side: tell the wallet which cluster this
        // transaction is for, the same way signAndSendTransaction does. `chain` is
        // OPTIONAL on SolanaSignTransactionInput (unlike the send input, where it is
        // required), so an app that named no network simply omits it rather than
        // failing closed - a plain sign puts nothing on a chain, so there is no
        // wrong cluster to protect against, only a hint to withhold. When a network
        // IS configured, pickSolanaAccount has already rejected any account that
        // does not carry that chain, so the hint can never contradict the account.
        async signTransaction(tx) {
            const acc = requireAccount();
            const sign = feature(wallet, SolanaSignTransaction);
            if (!sign)
                throw new Error("Wallet does not support solana:signTransaction");
            const { serializeUnsignedTx, deserializeSignedTx } = await import("./wallet-standard-tx");
            const [out] = await sign.signTransaction(signInputFor(acc, serializeUnsignedTx(tx)));
            return deserializeSignedTx(out.signedTransaction);
        },
        /** v1 goes to the wallet as raw SIMD-0385 bytes; the wallet must advertise version 1 to get one. */
        supportedTransactionVersions() {
            const sign = feature(wallet, SolanaSignTransaction);
            const advertised = sign === null || sign === void 0 ? void 0 : sign.supportedTransactionVersions;
            const list = advertised instanceof Set ? [...advertised] : Array.isArray(advertised) ? [...advertised] : [];
            const versions = list.filter((v) => v === 'legacy' || v === 0 || v === 1);
            // A wallet that advertises nothing is treated as legacy + v0, today's baseline.
            return versions.length > 0 ? versions : ['legacy', 0];
        },
        async signTransactionV1(tx) {
            const acc = requireAccount();
            const sign = feature(wallet, SolanaSignTransaction);
            if (!sign)
                throw new Error("Wallet does not support solana:signTransaction");
            const kit = await import('@solana/kit');
            const wire = Uint8Array.from(kit.getTransactionEncoder().encode(tx));
            const [out] = await sign.signTransaction(signInputFor(acc, wire));
            // Name the cause before parsing: kit's decoder would otherwise report a
            // misleading signature-count error on non-v1 bytes.
            if (!(out.signedTransaction instanceof Uint8Array) || out.signedTransaction[0] !== 0x81) {
                throw new Error('The wallet returned a non-v1 transaction for a SIMD-0385 v1 signing request.');
            }
            return kit.getTransactionDecoder().decode(out.signedTransaction);
        },
        async signAllTransactions(txs) {
            const acc = requireAccount();
            const sign = feature(wallet, SolanaSignTransaction);
            if (!sign)
                throw new Error("Wallet does not support solana:signTransaction");
            const { serializeUnsignedTx, deserializeSignedTx } = await import("./wallet-standard-tx");
            const outs = await sign.signTransaction(...txs.map((tx) => signInputFor(acc, serializeUnsignedTx(tx))));
            return outs.map((o) => deserializeSignedTx(o.signedTransaction));
        },
        // Asked LIVE, never snapshotted: MWA advertises both transaction features
        // up front and rewrites them during connect() once it learns what the wallet
        // actually supports. A capability read at construction time would send a
        // sign-only wallet down the native-send path and fail instead of using the
        // caller's sign-then-submit fallback.
        // Both of these answer "can this wallet do it, right now" and nothing more.
        // WHICH path to prefer is the caller's policy, and it lives with the caller:
        // InjectedWalletProvider signs and verifies before submitting whenever
        // signing is possible, because a native send is on the network before anyone
        // can check who signed it.
        canSignAndSend: () => {
            if (!hasFeature(wallet, SolanaSignAndSendTransaction))
                return false;
            // The wallet offering it is not enough: the ACCOUNT must too, or the
            // native call fails where the caller's sign-then-submit path would work.
            const acc = currentAccount();
            return !!acc && accountSupports(acc, SolanaSignAndSendTransaction);
        },
        canSignTransaction: () => {
            if (!hasFeature(wallet, SolanaSignTransaction))
                return false;
            const acc = currentAccount();
            return !!acc && accountSupports(acc, SolanaSignTransaction);
        },
        async signAndSendTransaction(tx) {
            const acc = requireAccount();
            const sas = feature(wallet, SolanaSignAndSendTransaction);
            if (!sas)
                throw new Error("Wallet does not support solana:signAndSendTransaction");
            const { serializeUnsignedTx } = await import("./wallet-standard-tx");
            const chain = resolveSolanaWalletStandardChain(configuredNetwork, "Wallet-Standard signAndSendTransaction");
            const [out] = await sas.signAndSendTransaction({
                account: acc,
                transaction: serializeUnsignedTx(tx),
                chain,
            });
            return { signature: bs58.encode(out.signature) };
        },
    };
}
/**
 * Enumerate every Solana-capable wallet currently registered with the Wallet
 * Standard, newest registrations included. Returns login-ready choices (name +
 * icon + provider factory). Empty array off-browser or when no wallet is present;
 * the widget then falls back to the legacy injected `window.solana` probe.
 */
export function discoverStandardWallets(configuredNetwork) {
    if (typeof window === "undefined")
        return [];
    const out = [];
    // Same name, different wallets is a real case (two SDKs on one page can each
    // register a "Mobile Wallet Adapter"). One entry per name still makes the
    // right list, but prefer a wallet that can actually work on the app's network
    // - otherwise an incompatible lookalike that registered first wins and every
    // transaction fails at the wallet.
    const chain = configuredNetwork ? solanaChainOrNull(configuredNetwork) : null;
    // Two wallets can share a display name (another SDK can register its own
    // "Mobile Wallet Adapter"). Keep ONE entry per name, but choose WHICH by
    // merit, not by who registered first: a wallet that supports the app's chain
    // beats one that does not, and among equals the mobile wallet WE registered
    // beats a stranger wearing its label - picking the stranger would drop the
    // gesture its transport needs and can hide the legacy probe. Names keep their
    // first-seen order, so the list the user sees is otherwise unchanged.
    const rank = (w) => (chain && !w.chains.includes(chain) ? 2 : 0) + (isSolanaMobileWallet(w) ? 0 : 1);
    const bestByName = new Map();
    const nameOrder = [];
    for (const wallet of getWallets().get()) {
        if (!isLoginCapable(wallet))
            continue;
        // A wallet that does not carry the app's chain at all cannot serve it. That
        // is knowable here, so drop it rather than listing it and failing at connect
        // - or worse, letting the no-injection fallback default to it.
        if (chain && !wallet.chains.includes(chain))
            continue;
        const incumbent = bestByName.get(wallet.name);
        if (!incumbent) {
            bestByName.set(wallet.name, wallet);
            nameOrder.push(wallet.name);
        }
        else if (rank(wallet) < rank(incumbent)) {
            bestByName.set(wallet.name, wallet);
        }
    }
    for (const name of nameOrder) {
        const wallet = bestByName.get(name);
        out.push({
            name: wallet.name,
            icon: wallet.icon,
            wallet,
            // DS2-0383: the adapted provider signs+sends on the app's configured
            // network, never on the wallet's first advertised chain. The adapter is
            // stateless (it reads the wallet's live accounts), so handing out a fresh
            // one per call costs nothing and keeps no state to go stale.
            getProvider: () => standardWalletToInjectedProvider(wallet, configuredNetwork),
        });
    }
    return out;
}
/**
 * First login-capable Wallet Standard wallet adapted to the injected-provider
 * shape, or null. The last-resort fallback for wallet login when nothing is
 * injected - e.g. a Saga/Seeker phone, where the Solana Mobile wallet lives in
 * the registry (via registerMwa) and never as `window.solana`.
 */
export function firstStandardWalletProvider(configuredNetwork) {
    const [first] = discoverStandardWallets(configuredNetwork);
    return first ? first.getProvider() : null;
}
